import { useNavigate } from 'react-router-dom';
import { Factory, LogOut, Monitor, Moon, Sun, User } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { useAuthStore } from '@features/authentication/store/authStore';
import { useTheme } from '@/components/theme/useTheme';
import type { Theme } from '@/components/theme/theme-context';

/**
 * The rail's avatar and everything the retired sidebar footer used to reach (M7 · 86d44z2ff).
 *
 * <b>Why this ticket exists at all:</b> there is no top bar, no settings page and no command
 * palette in this app — `AppLayout` renders the shell chrome and an `<Outlet/>`, nothing else. So
 * the old sidebar footer was the SOLE route to logout, to `/profile` (and therefore to the
 * change-password card and the push-notifications card), and to theming. The design's rail avatar
 * is a non-interactive `<span>`; shipping that would make all of it URL-only and unreachable by
 * keyboard, when users can log out with the keyboard today.
 *
 * ⚠️ The trigger and the menu deliberately show DIFFERENT strings, matching what shipped: the
 * trigger identifies the person (`fullName || username`), the menu identifies the ACCOUNT
 * (`username` + email + a badge per role). That is not drift — you hover to check who you are, and
 * open to check which account you are signed into.
 */
export function RailProfileMenu() {
  const { user, logout } = useAuthStore();
  const { theme, setTheme } = useTheme();
  const navigate = useNavigate();

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  // Initials = first + last initial; fall back to the first two chars of the username when the
  // user has no details record.
  const first = user?.firstName?.trim();
  const last = user?.lastName?.trim();
  const initials = (
    first || last ? `${first?.[0] ?? ''}${last?.[0] ?? ''}` : (user?.username?.slice(0, 2) ?? '')
  ).toUpperCase();
  const displayName = user?.fullName?.trim() || user?.username || 'Unknown';
  const primaryRole = user?.roles?.[0];

  /* ⚠️ The design's tooltip reads "Arnel D. — Sales Coordinator", i.e. name + JOB TITLE. There is
     no title field: `UserInfoDto` carries username, email, firstName, lastName, fullName and roles.
     Adding one is a CORE + API change, out of scope here — so the tip uses the primary ROLE, which
     is what the shipped trigger already displayed and costs no new fetch. */
  const tip = primaryRole ? `${displayName} — ${primaryRole}` : displayName;

  return (
    <DropdownMenu>
      <Tooltip delayDuration={0}>
        <TooltipTrigger asChild>
          <DropdownMenuTrigger asChild>
            {/* Must-fix 1: a real <button>, not the design's <span>. Icon-only in the rail, so it
                carries a Tooltip AND a matching aria-label (UI gate §5). */}
            <button
              type="button"
              aria-label={`${tip} — account menu`}
              className="mt-0.5 flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-full transition-colors hover:bg-sidebar-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sidebar-ring"
            >
              {/* White avatar + brand-red initials is a §3 brand-fixed exception: it must read as
                  an avatar on every sidebar surface, including the charcoal Industrial rail. */}
              <Avatar className="h-8 w-8 border border-sidebar-border">
                <AvatarFallback className="bg-white text-sm font-semibold text-capitol-red">
                  {initials || <User className="h-4 w-4" />}
                </AvatarFallback>
              </Avatar>
            </button>
          </DropdownMenuTrigger>
        </TooltipTrigger>
        <TooltipContent side="right" className="font-medium">
          {tip}
        </TooltipContent>
      </Tooltip>

      {/* ⚠️ `data-surface="sidebar"` on the PORTALLED content. Radix attaches this to document.body,
          outside the rail, so without it Industrial paints a light-canvas menu hanging off a
          charcoal rail. This menu is ours, so it can carry the attribute — the notification row's
          ⋯ menu cannot, which is why that one is documented as a known inconsistency instead. */}
      <DropdownMenuContent
        side="right"
        align="end"
        data-surface="sidebar"
        className="w-60 border-sidebar-border bg-sidebar text-sidebar-foreground"
      >
        <DropdownMenuLabel>
          <div className="flex flex-col space-y-1">
            <p className="text-sm font-medium">{user?.username ?? 'Unknown'}</p>
            <p className="text-xs text-muted-foreground">{user?.email ?? ''}</p>
            {user?.roles && user.roles.length > 0 && (
              <div className="flex flex-wrap gap-1 pt-1">
                {user.roles.map((role) => (
                  <Badge key={role} variant="secondary" className="px-1.5 py-0 text-2xs">
                    {role}
                  </Badge>
                ))}
              </div>
            )}
          </div>
        </DropdownMenuLabel>

        <DropdownMenuSeparator className="bg-sidebar-border" />
        {/* ⚠️ There is NO change-password ITEM — password change is a Card ON the profile page, and
            so is the push-notifications card. This one link is the route to all three. */}
        <DropdownMenuItem className="cursor-pointer" onClick={() => navigate('/profile')}>
          <User className="mr-2 h-4 w-4" />
          My Profile
        </DropdownMenuItem>

        <DropdownMenuSeparator className="bg-sidebar-border" />
        <DropdownMenuLabel className="text-2xs uppercase tracking-wider text-muted-foreground">
          Appearance
        </DropdownMenuLabel>
        {/* ⚠️ FOUR items, not three. The app stores four values and defaults to `system`, but the
            control this replaces was a CYCLE button over the three PAINTED themes — so once a user
            clicked it even once, `system` became unreachable for the rest of that browser's life.
            A radio group is what makes the stored vocabulary and the control agree, and the checked
            state reads the stored PREFERENCE (`theme`), never the resolved one: with `system`
            selected, checking `resolvedTheme` would tick Light or Dark instead. */}
        <DropdownMenuRadioGroup value={theme} onValueChange={(v) => setTheme(v as Theme)}>
          {THEME_OPTIONS.map(({ value, label, icon: Icon }) => (
            <DropdownMenuRadioItem key={value} value={value} className="cursor-pointer">
              <Icon className="mr-2 h-4 w-4" />
              {label}
            </DropdownMenuRadioItem>
          ))}
        </DropdownMenuRadioGroup>

        <DropdownMenuSeparator className="bg-sidebar-border" />
        <DropdownMenuItem
          className="cursor-pointer text-destructive focus:text-destructive"
          onClick={handleLogout}
        >
          <LogOut className="mr-2 h-4 w-4" />
          Logout
        </DropdownMenuItem>

        {/* Owner decision 2026-08-26: the version lives HERE, not in the module panel footer — that
            footer only exists while the panel is open, and the panel is now a remembered per-user
            preference, so a collapsed panel would leave a signed-in user with no version readout at
            all. Support asks "what version are you on?" and this is the answer.
            ⚠️ Safe to move: the stale-tab update beacon compares fetched /version.json against the
            build-time __APP_VERSION__ and never reads this DOM. */}
        <p className="px-2 pb-1 pt-2 text-center text-2xs text-sidebar-foreground/60">
          DOP v{__APP_VERSION__}
        </p>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

const THEME_OPTIONS: { value: Theme; label: string; icon: LucideIcon }[] = [
  { value: 'light', label: 'Light', icon: Sun },
  { value: 'dark', label: 'Dark', icon: Moon },
  { value: 'industrial', label: 'Industrial', icon: Factory },
  { value: 'system', label: 'System', icon: Monitor },
];
